import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPatch, ApiError } from "../api/client.js";

export interface AgentMatchSuggestion {
  id: string;
  name: string;
  category: string;
  scope: "public" | "company";
  confidence: number;
  rationale: string;
}

interface SmartMatchResult {
  suggestions: AgentMatchSuggestion[];
  message: string;
  noMatch: boolean;
  matchType?: "agent" | "human";
}

interface Props {
  stepId: string;
  stepTitle: string;
  stepType: string;
  workspaceId: string;
  onClose: () => void;
  onAssigned: () => void;
}

function ConfidenceBadge({ value }: { value: number }) {
  const color = value >= 75 ? "var(--green, #16a34a)" : value >= 50 ? "var(--yellow, #d97706)" : "var(--text-3)";
  return (
    <span style={{ fontWeight: 700, color, marginLeft: 6 }}>
      {value}% match
    </span>
  );
}

export function AgentMatchModal({ stepId, stepTitle, stepType, workspaceId, onClose, onAssigned }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SmartMatchResult | null>(null);
  const [selected, setSelected] = useState<AgentMatchSuggestion | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runMatch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiPost<SmartMatchResult>(`/api/steps/${stepId}/smart-match`, {});
      setResult(data);
      if (data.suggestions.length > 0) setSelected(data.suggestions[0] ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        const msg = (err.body as { message?: string } | null)?.message;
        setError(msg ?? "Could not load suggestions. Try again or pick manually.");
      } else {
        setError("Could not load suggestions. Try again or pick manually.");
      }
    } finally {
      setLoading(false);
    }
  }, [stepId]);

  // Auto-run on first render
  const [started, setStarted] = useState(false);
  if (!started) {
    setStarted(true);
    runMatch();
  }

  const handleAssign = useCallback(async () => {
    if (!selected) return;
    setAssigning(true);
    setError(null);
    try {
      const isHuman = result?.matchType === "human";
      if (isHuman) {
        await apiPatch(`/api/steps/${stepId}/assign`, { mode: "user", user_id: selected.id });
      } else {
        await apiPatch(`/api/steps/${stepId}/assign`, { mode: "agent", hand_id: selected.id });
        // Agent assignment also auto-installs the hand into the workspace
        // (server side). Refresh inventories so the Library badge, My
        // Hands list, and the top-bar fleet count update without a hard
        // page reload.
        qc.invalidateQueries({ queryKey: ["hands", "my", workspaceId] });
        qc.invalidateQueries({ queryKey: ["hands", "library", workspaceId] });
      }
      onAssigned();
      onClose();
    } catch {
      setError("Failed to assign. Try again.");
      setAssigning(false);
    }
  }, [selected, result, stepId, workspaceId, onAssigned, onClose, qc]);

  const handleGoToBuilder = useCallback(() => {
    onClose();
    // Pass the step title as a prefill hint so the drafts page can
    // auto-create a draft with a meaningful starting name instead of
    // dropping the user on "Untitled Hand".
    const qs = new URLSearchParams({
      prefill_name: stepTitle,
      prefill_description: `Built to handle: ${stepTitle} (${stepType} step)`,
    }).toString();
    navigate(`/workspaces/${workspaceId}/hands/builder?${qs}`);
  }, [navigate, workspaceId, onClose, stepTitle, stepType]);

  const modal = (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.45)", display: "flex",
        alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--surface, #fff)", borderRadius: 12,
          width: 520, maxHeight: "80vh", display: "flex",
          flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>
            {result?.matchType === "human" ? "Smart People Matching" : "Smart Agent Matching"}
          </div>
          <div style={{ fontWeight: 600, color: "var(--text-1)" }}>{stepTitle}</div>
          <div style={{ color: "var(--text-3)", marginTop: 2 }}>{stepType} step</div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading && (
            <div style={{ textAlign: "center", color: "var(--text-3)" }}>
              ⚡ Analyzing step and searching agents…
            </div>
          )}

          {error && (
            <div style={{ background: "var(--red-soft, #fef2f2)", border: "1px solid var(--red, #dc2626)", borderRadius: 8, color: "var(--red, #dc2626)", marginBottom: 12 }}>
              {error}
            </div>
          )}

          {result && !loading && (
            <>
              <div style={{ color: "var(--text-2)", marginBottom: 14 }}>{result.message}</div>

              {result.suggestions.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {result.suggestions.map((s) => (
                    <div
                      key={s.id}
                      onClick={() => setSelected(s)}
                      style={{
                        border: `1.5px solid ${selected?.id === s.id ? "var(--accent, #6366f1)" : "var(--border)"}`,
                        borderRadius: 8, padding: "10px 14px", cursor: "pointer",
                        background: selected?.id === s.id ? "color-mix(in srgb, var(--accent, #6366f1) 6%, transparent)" : "var(--surface-2, #f9f9f9)",
                        transition: "border-color 150ms, background 150ms",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600 }}>{s.name}</span>
                        <ConfidenceBadge value={s.confidence} />
                        <span style={{ marginLeft: "auto", color: "var(--text-3)", textTransform: "uppercase" }}>
                          {s.scope === "public" ? "Library" : "Company"}
                        </span>
                      </div>
                      <div style={{ color: "var(--text-2)", lineHeight: 1.5 }}>{s.rationale}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", color: "var(--text-3)" }}>
                  No close matches. Build an agent or pick one manually.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center" }}>
          <button
            type="button"
            className="btn primary"
            disabled={!selected || assigning}
            onClick={handleAssign}
          >
            {assigning ? "Assigning…" : selected ? `Assign ${selected.name}` : result?.matchType === "human" ? "Select a person" : "Select an agent"}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Skip for now
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={handleGoToBuilder}>
            + Agent Builder
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
